import { useEffect, useRef } from "react";

interface InfiniteScrollProps {
  loadMore: () => void;
  hasMore: boolean;
  loading?: boolean;
  threshold?: number;
  children: React.ReactNode;
}

export default function InfiniteScroll({
  loadMore,
  hasMore,
  loading = false,
  threshold = 200,
  children,
}: InfiniteScrollProps) {
  const loaderRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (loading || !hasMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const target = entries[0];
        if (target.isIntersecting) {
          loadMore();
        }
      },
      {
        root: null,
        rootMargin: `0px 0px ${threshold}px 0px`,
        threshold: 0,
      },
    );

    const node = loaderRef.current;
    if (node) observer.observe(node);

    return () => {
      if (node) observer.unobserve(node);
      observer.disconnect();
    };
  }, [loading, hasMore, threshold, loadMore]);

  return (
    <div className="relative overflow-auto">
      <div>{children}</div>
      <div ref={loaderRef} className="h-0.5 w-full" />
    </div>
  );
}
